import { useMemo } from 'react';

import { CompoundSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { useDualGrantorOptions } from '@/hooks/useDualGrantorOptions';
import {
  HypotheticalInsuranceTransferTaxKind,
  InsurancePolicyPremiumPaymentTransferTaxKind,
} from '@/types/schema';

/**
 * See the definition of [InsurancePolicyDetails.premiumPaymentTransferTaxKind](../InsurancePolicyDetailsSubform.types.ts) for more information.
 *
 * @returns The options for a premium payment transfer kind select input
 */
export function useInsurancePolicyDetailsPremiumPaymentTransferKindOptions(
  isHypothetical: boolean | undefined = false
): CompoundSelectInputOption<string>[] {
  const grantorOptions = useDualGrantorOptions();

  return useMemo<CompoundSelectInputOption<string>[]>(
    () => [
      {
        type: 'component',
        component: <SelectItemGroupLabel label="Non-taxable gifts" />,
      },
      {
        display: 'Annual exclusion gift with Crummey powers',
        value: isHypothetical
          ? HypotheticalInsuranceTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers
          : InsurancePolicyPremiumPaymentTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers,
      },
      {
        display: 'Non-taxable transfer',
        value: isHypothetical
          ? HypotheticalInsuranceTransferTaxKind.NonTaxable
          : InsurancePolicyPremiumPaymentTransferTaxKind.NonTaxable,
      },
      {
        component: <SelectItemGroupLabel label="Taxable gift from" />,
        type: 'component',
      },
      ...grantorOptions,
    ],
    [grantorOptions, isHypothetical]
  );
}
